import styled from "styled-components";
import { ChangeEvent, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import Text from "@/components/Text";
import {
  InputGroup,
  ResetButton,
  ResetHolder,
  StyledInput,
  Wrapper,
} from "@/pages/AssignTopics/components/Filter";
import { selectTeamSettings } from "@/selectors";
import { Dispatch, RootState } from "@/utils/store";
import useDebounce from "@/hooks/useDebounce";
import useDidUpdate from "@/hooks/useDidUpdate";
import {
  ensureStringContainsDecimalNumbersOnly,
  ensureStringContainsPositiveDigitsOnly,
} from "@/utils/helpers";

const Wrap = styled.div`
  display: flex;
  justify-content: flex-end;
  flex: 1;
  gap: 16px;
`;

interface State {
  minDuration: string;
  maxDuration: string;
  minViews: string;
  minV7Index: string;
}

const TopVideosFilter = () => {
  const dispatch = useDispatch<Dispatch>();

  const { teamId } = useParams();

  const teamSettings = useSelector((rootState: RootState) =>
    selectTeamSettings(rootState, teamId!)
  );

  const [values, setValues] = useState<State>({
    minDuration: teamSettings?.top_videos_min_duration ?? "",
    maxDuration: teamSettings?.top_videos_max_duration ?? "",
    minViews: teamSettings?.top_videos_min_views ?? "",
    minV7Index: teamSettings?.top_videos_min_v7_index ?? "",
  });

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name !== "minV7Index" && !ensureStringContainsPositiveDigitsOnly(value))
      return;

    if (name === "minV7Index" && !ensureStringContainsDecimalNumbersOnly(value))
      return;

    setValues((prev) => ({ ...prev, [name as keyof State]: value }));
  };

  const debouncedValues = useDebounce(values, 500);

  const resetValue = (key: keyof State) =>
    setValues((prev) => ({ ...prev, [key]: "" }));

  useDidUpdate(() => {
    dispatch.teams.updateSettings({
      teamId: teamId!,
      updatedSettings: {
        top_videos_min_duration: debouncedValues.minDuration,
        top_videos_max_duration: debouncedValues.maxDuration,
        top_videos_min_views: debouncedValues.minViews,
        top_videos_min_v7_index: debouncedValues.minV7Index,
      },
    });
  }, [debouncedValues, teamId]);

  return (
    <Wrap>
      <InputGroup>
        <Text $type="smallHeadline">Minimum Duration (sec)</Text>
        <Wrapper>
          <ResetHolder>
            <ResetButton onClick={() => resetValue("minDuration")}>
              Reset
            </ResetButton>
          </ResetHolder>
          <StyledInput
            placeholder="Enter number"
            name="minDuration"
            value={values.minDuration}
            onChange={onChange}
          />
        </Wrapper>
      </InputGroup>
      <InputGroup>
        <Text $type="smallHeadline">Maximum Duration (sec)</Text>
        <Wrapper>
          <ResetHolder>
            <ResetButton onClick={() => resetValue("maxDuration")}>
              Reset
            </ResetButton>
          </ResetHolder>
          <StyledInput
            placeholder="Enter number"
            name="maxDuration"
            value={values.maxDuration}
            onChange={onChange}
          />
        </Wrapper>
      </InputGroup>
      <InputGroup>
        <Text $type="smallHeadline">Minimum Views</Text>
        <Wrapper>
          <ResetHolder>
            <ResetButton onClick={() => resetValue("minViews")}>
              Reset
            </ResetButton>
          </ResetHolder>
          <StyledInput
            placeholder="Enter number"
            name="minViews"
            value={values.minViews}
            onChange={onChange}
          />
        </Wrapper>
      </InputGroup>
      <InputGroup>
        <Text $type="smallHeadline">Minimum V7 Index</Text>
        <Wrapper>
          <ResetHolder>
            <ResetButton onClick={() => resetValue("minV7Index")}>
              Reset
            </ResetButton>
          </ResetHolder>
          <StyledInput
            placeholder="Enter number"
            name="minV7Index"
            value={values.minV7Index}
            onChange={onChange}
          />
        </Wrapper>
      </InputGroup>
    </Wrap>
  );
};

export default TopVideosFilter;
